import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { IonButton } from '@ionic/react';
import { useToast } from 'contexts/ToastContext';
import billingService from 'services/billing';
import Addons from 'services/addons';
import Credits, { CREDIT_CHANGED_EVENT, formatBalance } from 'services/credits';
import AddCreditsModal from 'components/billing/AddCreditsModal';
import AddonCard, { QuantityStepper, CopyButton } from 'components/billing/AddonCard';
import './BillingPage.css';

// Heuristic shown under the big balance number.  We picked the Light
// Managed App tier ($0.05/hr) as the "if always on" example — it's the
// cheapest tier so the runtime estimate stays optimistic and is honest
// about being a lower bound. Future work: derive this from the user's
// own currently-running things.
const HEURISTIC_BURN_USD_PER_HOUR = 0.05;
const HEURISTIC_LABEL = 'Light Managed App, always on';

// A day of runway at the heuristic burn rate, expressed in milli-cents
// ($1 = 100_000 milli-cents).  Used to drive the "Add credits to
// enable" inline warning on addon cards — if the user's balance is
// below a single day of even the cheapest baseline, enabling a paid
// addon is almost certain to 402 within hours.
const ONE_DAY_MILLI_CENTS = HEURISTIC_BURN_USD_PER_HOUR * 24 * 100000;

const formatUsd = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '$0.00';
  return `$${v.toFixed(2)}`;
};

const BillingPage = () => {
  const [billingData, setBillingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addCreditsOpen, setAddCreditsOpen] = useState(false);
  const [addonBusy, setAddonBusy] = useState({ egress: false, warm: false });
  const ledgerRef = useRef(null);
  const { showToast } = useToast();
  const location = useLocation();

  const loadBillingData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await billingService.getBillingInfo();
      setBillingData(data);
    } catch (error) {
      console.error('Error loading billing data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Mount effect: load data + handle Stripe-checkout return.
  // The credits success URL pattern is `?checkout=success&session_id=X`
  // (set in AddCreditsModal). We show a toast, strip the query, and
  // refresh after 2s so the webhook has time to post the ledger entry
  // before we re-read the balance.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const checkoutParam = urlParams.get('checkout');

    if (checkoutParam === 'success') {
      try { showToast('Credits added — thanks!', 'success'); } catch {}
      window.history.replaceState({}, document.title, location.pathname);
      setTimeout(() => Credits.fireChangedEvent(), 2000);
      loadBillingData();
      return;
    }
    loadBillingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch whenever a credit-affecting event fires (top-up returns,
  // addon enable/disable on another tab). Same hook the header chip
  // uses — keeps everything in sync without polling.
  useEffect(() => {
    const handler = () => loadBillingData();
    window.addEventListener(CREDIT_CHANGED_EVENT, handler);
    return () => window.removeEventListener(CREDIT_CHANGED_EVENT, handler);
  }, [loadBillingData]);

  const credits = billingData?.credits || null;
  const addons = billingData?.addons || { dedicatedEgress: {}, warmPool: {} };
  const topupHistory = billingData?.topupHistory || [];
  const recentLedger = billingData?.recentLedger || [];

  const balanceMc = Number(credits?.balanceMilliCents) || 0;
  const balanceUsd = balanceMc / 100000;
  const hoursOfRuntime = balanceUsd > 0
    ? Math.round(balanceUsd / HEURISTIC_BURN_USD_PER_HOUR)
    : 0;
  const insufficient = balanceMc < ONE_DAY_MILLI_CENTS;

  const scrollToLedger = () => {
    ledgerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ── Dedicated Egress action handlers ────────────────────────────────
  const onToggleEgress = async () => {
    const isEnabled = !!addons.dedicatedEgress?.enabled;
    setAddonBusy((s) => ({ ...s, egress: true }));
    try {
      if (isEnabled) {
        await Addons.disableDedicatedEgress();
        showToast('Dedicated egress disabled', 'success');
      } else {
        await Addons.enableDedicatedEgress();
        showToast('Dedicated egress enabled — provisioning IP…', 'success');
      }
      Credits.fireChangedEvent();
      await loadBillingData();
    } catch (err) {
      const msg = err?.body?.message || err?.message || 'Action failed';
      showToast(msg, 'error');
    } finally {
      setAddonBusy((s) => ({ ...s, egress: false }));
    }
  };

  // ── Warm Pool action handlers ───────────────────────────────────────
  const onToggleWarmPool = async () => {
    const isEnabled = !!addons.warmPool?.enabled;
    setAddonBusy((s) => ({ ...s, warm: true }));
    try {
      if (isEnabled) {
        await Addons.disableWarmPool();
        showToast('Warm pool disabled', 'success');
      } else {
        // Default to 1 instance on first enable — user can scale up
        // with the quantity stepper once it renders enabled.
        await Addons.enableWarmPool(1);
        showToast('Warm pool enabled', 'success');
      }
      Credits.fireChangedEvent();
      await loadBillingData();
    } catch (err) {
      const msg = err?.body?.message || err?.message || 'Action failed';
      showToast(msg, 'error');
    } finally {
      setAddonBusy((s) => ({ ...s, warm: false }));
    }
  };

  const onChangeWarmQuantity = async (nextQuantity) => {
    setAddonBusy((s) => ({ ...s, warm: true }));
    try {
      await Addons.enableWarmPool(nextQuantity);
      showToast(`Warm pool scaled to ${nextQuantity}`, 'success');
      Credits.fireChangedEvent();
      await loadBillingData();
    } catch (err) {
      const msg = err?.body?.message || err?.message || 'Scale failed';
      showToast(msg, 'error');
    } finally {
      setAddonBusy((s) => ({ ...s, warm: false }));
    }
  };

  // ── Card detail row helpers ─────────────────────────────────────────
  const egress = addons.dedicatedEgress || {};
  const egressPerDay = Number(egress.perDayUsd);
  const egressDetailRows = egress.enabled
    ? [
      ['IP address', egress.eipAddress ? (
        <span className="addon-ip">
          <code>{egress.eipAddress}</code>
          <CopyButton text={egress.eipAddress} />
        </span>
      ) : 'Provisioning…'],
    ]
    : [];

  const warm = addons.warmPool || {};
  const warmQty = Number(warm.quantity) || 0;
  const warmPerDayEach = Number(warm.perInstancePerDayUsd);
  const warmDetailRows = warm.enabled
    ? [
      ['Current instances', warmQty.toString()],
      ...(Number.isFinite(warmPerDayEach)
        ? [['Total per day', `$${(warmPerDayEach * warmQty).toFixed(2)}`]]
        : []),
    ]
    : [];

  return (
    <>
      <Helmet>
        <title>Billing - Zibby</title>
        <meta name="description" content="Manage your Zibby credit balance and add-ons." />
      </Helmet>
      <div className="billing-page page-fade-in main-body page-layout">
        <div className="page-header-center">
          <h1>Billing</h1>
          <p>Credit balance, add-ons, and payment history</p>
        </div>

        {/* ── A. BALANCE CARD ─────────────────────────────────────────── */}
        <div className="page-card balance-hero">
          <div className="balance-hero-body">
            <div className="balance-hero-label">Balance</div>
            <div className={`balance-hero-amount ${balanceMc < 0 ? 'is-paused' : balanceMc < 100000 ? 'is-low' : ''}`}>
              {loading ? '—' : formatBalance(balanceMc)}
            </div>
            <div className="balance-hero-runtime">
              {loading
                ? ' '
                : balanceUsd > 0
                  ? `~ ${hoursOfRuntime} hours of runtime (${HEURISTIC_LABEL}, $${HEURISTIC_BURN_USD_PER_HOUR.toFixed(2)}/hr)`
                  : 'Add credits to start running workflows and apps.'}
            </div>
          </div>
          <div className="balance-hero-actions">
            {/* Single CTA — Recent activity section below shows the ledger,
                so a "View history" button here is redundant. The shared
                .btn-white atom is an IonButton class — its --background
                etc. CSS vars don't apply to a plain <button> so we use
                IonButton here to match the project-settings + sentry
                page pattern. */}
            <IonButton
              fill="clear"
              size="small"
              className="btn-white"
              onClick={() => setAddCreditsOpen(true)}
            >
              Add credits
            </IonButton>
          </div>
        </div>

        {/* ── B. ADDONS SECTION ──────────────────────────────────────── */}
        <div className="page-card addons-section">
          <div className="card-header">
            <h2>Add-ons</h2>
          </div>
          <p className="addons-section-blurb">
            Optional infrastructure billed per-minute from your credit balance. Disable any time.
          </p>

          <div className="addons-grid">
            <AddonCard
              name="Dedicated Static IP"
              description="Pin all outbound traffic to one static IP so you can allowlist Zibby on a firewalled GitLab / GitHub Enterprise / Salesforce."
              perDayUsd={Number.isFinite(egressPerDay) ? egressPerDay : 1.67}
              perMonthEstimateUsd={Number.isFinite(egressPerDay) ? egressPerDay * 30 : 50}
              enabled={!!egress.enabled}
              detailRows={egressDetailRows}
              insufficientBalance={insufficient}
              busy={addonBusy.egress}
              onEnable={onToggleEgress}
              onDisable={onToggleEgress}
              onAddCredits={() => setAddCreditsOpen(true)}
            />

            <AddonCard
              name="Warm Pool"
              description="Pre-running Fargate tasks so workflow runs skip the ~15s cold-start. Charged per-instance, per-minute."
              perDayUsd={Number.isFinite(warmPerDayEach) ? warmPerDayEach : 0.83}
              perMonthEstimateUsd={Number.isFinite(warmPerDayEach) ? warmPerDayEach * 30 : 25}
              enabled={!!warm.enabled}
              detailRows={warmDetailRows}
              insufficientBalance={insufficient}
              busy={addonBusy.warm}
              onEnable={onToggleWarmPool}
              onDisable={onToggleWarmPool}
              onAddCredits={() => setAddCreditsOpen(true)}
            >
              {warm.enabled && (
                <div className="addon-card-quantity-row">
                  <span className="addon-card-detail-label">Instances</span>
                  <QuantityStepper
                    value={warmQty || 1}
                    min={1}
                    max={10}
                    onChange={onChangeWarmQuantity}
                    busy={addonBusy.warm}
                  />
                </div>
              )}
            </AddonCard>
          </div>
        </div>

        {/* ── C. TOP-UP HISTORY ──────────────────────────────────────── */}
        <div className="page-card topup-history" ref={ledgerRef}>
          <div className="card-header">
            <h2>Top-up history</h2>
          </div>

          {topupHistory.length === 0 ? (
            <p className="topup-empty">
              No top-ups yet. Click "Add credits" above to fund your account.
            </p>
          ) : (
            <table className="topup-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th className="num">Amount</th>
                  <th>Status</th>
                  <th>Receipt</th>
                </tr>
              </thead>
              <tbody>
                {topupHistory.map((t, idx) => (
                  <tr key={t.id || `${t.ts || ''}-${idx}`}>
                    <td>{t.ts || t.date ? new Date(t.ts || t.date).toLocaleDateString() : '—'}</td>
                    <td className="num">{formatUsd(t.amountUsd)}</td>
                    <td>
                      <span className={`status-badge status-${(t.status || 'paid').toLowerCase()}`}>
                        {t.status || 'paid'}
                      </span>
                    </td>
                    <td>
                      {t.stripeReceiptUrl || t.receiptUrl ? (
                        <a
                          className="btn-link"
                          href={t.stripeReceiptUrl || t.receiptUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Download
                        </a>
                      ) : (
                        <span className="btn-link disabled">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ── D. LEDGER ──────────────────────────────────────────────── */}
        <div className="page-card ledger-section">
          <div className="card-header">
            <h2>Recent activity</h2>
          </div>
          {recentLedger.length === 0 ? (
            <p className="topup-empty">No ledger entries yet.</p>
          ) : (
            <div className="credits-ledger-scroll">
              <table className="credits-ledger-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Reason</th>
                    <th>Source</th>
                    <th className="num">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {recentLedger.map((r, i) => {
                    const delta = Number(r.deltaMilliCents) || 0;
                    const isCredit = delta > 0;
                    return (
                      <tr key={`${r.ts || ''}-${i}`}>
                        <td>{r.ts ? new Date(r.ts).toLocaleString() : '—'}</td>
                        <td>{r.reason || '—'}</td>
                        <td className="muted">{r.source || '—'}</td>
                        <td className={`num ${isCredit ? 'is-credit' : 'is-debit'}`}>
                          {isCredit ? '+' : ''}{formatBalance(delta, { precision: 4 })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Add-credits modal — single instance, opened from balance hero
            or from the inline "Add credits" CTA on insufficient-balance
            addon warnings. */}
        {addCreditsOpen && (
          <AddCreditsModal
            isOpen={addCreditsOpen}
            onClose={() => setAddCreditsOpen(false)}
          />
        )}
      </div>
    </>
  );
};

export default BillingPage;
